// src/theme/muiTheme.ts
//
// FOUNDATION WAVE — a deliberately THIN, PLACEHOLDER MUI theme. Its only job is to give MUI
// surfaces (DataGridPro today; dialogs/steppers later) a neutral base so they sit quietly next
// to the Tailwind/Shadcn SPA. The real fleetGuardian brand palette lands in the NEXT wave —
// swap the palette values below then and every MUI surface updates at once.
//
// Pairs with `<StyledEngineProvider injectFirst>` + NO `<CssBaseline>` in App.tsx:
//   - injectFirst puts MUI's Emotion styles FIRST in <head>, so Tailwind utilities win on
//     class collisions (Tailwind is loaded after and its specificity ties are broken by order).
//   - skipping CssBaseline avoids MUI injecting Roboto + a global reset that would fight
//     Tailwind's own preflight reset. This mirrors the proven fleetGuardian setup.

import { createTheme, type Theme } from '@mui/material/styles';

export type MuiMode = 'light' | 'dark';

/**
 * Build a thin MUI theme for the given colour mode. Its whole job is to make MUI surfaces
 * (DataGridPro today; dialogs later) adopt the fleetGUARDIAN navy + Inter instead of MUI's stock
 * blue + Roboto — palette + typography + radius, nothing else (component looks live in the
 * Tailwind primitives + the per-grid `dataGridSx`, never in `components.MuiX.styleOverrides`).
 *
 * Kept as a factory so the ThemeProvider tracks the SPA's own light/dark toggle and the
 * DataGridPro never renders as a dark island on a light page (or vice-versa).
 */
export function makeMuiTheme(mode: MuiMode): Theme {
  const dark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      primary: {
        main: dark ? '#3b82f6' : '#1B4F83', // brand navy (lifted for contrast on dark)
        dark: '#16406A',
        contrastText: '#FFFFFF',
      },
      error: { main: '#B91C1C' },
      success: { main: '#047857' },
      warning: { main: '#B45309' },
      info: { main: '#1D4ED8' },
    },
    typography: {
      fontFamily:
        "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    },
    shape: { borderRadius: 8 },
  });
}

/** Default theme is LIGHT, matching the fleetGUARDIAN source app. */
export const muiTheme = makeMuiTheme('light');

export default muiTheme;
